import { useState, useRef, useEffect } from 'react';
import {
  User,
  Settings,
  LogOut,
  ChevronDown,
  Star,
  Heart,
  Receipt,
  MapPin,
  CreditCard,
  Gift,
  Leaf,
  Drumstick,
  Utensils,
} from 'lucide-react';
import type { UserType } from '../../App';
import { api } from '../../lib/api';
import { useLocale } from '../../lib/locale';
import { ThemeModeToggle } from './ThemeModeToggle';

interface ProfileDropdownProps {
  user: UserType;
  onLogout: () => void;
  onSignIn?: () => void;
  onOpenSettings?: (section?: string) => void;
  onOpenOrderHistory?: () => void;
  variant?: 'default' | 'compact';
}

export function ProfileDropdown({
  user,
  onLogout,
  onSignIn,
  onOpenSettings,
  onOpenOrderHistory,
  variant = 'default',
}: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t, formatCompactMoney } = useLocale();
  const hasToken = Boolean(localStorage.getItem('auth_token') || localStorage.getItem('token'));
  const isGuestUser = !hasToken || String(user?.id || '') === 'guest';
  const [dietaryMode, setDietaryMode] = useState<'all' | 'veg' | 'non-veg'>(() => {
    return (localStorage.getItem('dietaryMode') as 'all' | 'veg' | 'non-veg') || 'all';
  });
  const FAVORITES_STORAGE_KEY = 'customer_favorite_restaurants';
  const [quickStats, setQuickStats] = useState({ orders: 0, favorites: 0, saved: 0 });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleDietaryModeChange = (mode: 'all' | 'veg' | 'non-veg') => {
    setDietaryMode(mode);
    localStorage.setItem('dietaryMode', mode);
    // Dispatch a custom event to notify other components
    window.dispatchEvent(new CustomEvent('dietaryModeChange', { detail: mode }));
  };

  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;

    const loadStats = async () => {
      let favoritesCount = 0;
      try {
        const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : [];
        favoritesCount = Array.isArray(parsed) ? parsed.length : 0;
      } catch {
        favoritesCount = 0;
      }

      if (!hasToken) {
        if (mounted) setQuickStats({ orders: 0, favorites: favoritesCount, saved: 0 });
        return;
      }

      try {
        const response = await api.orders.getMyOrders({ page: 1, limit: 100 });
        const orders = Array.isArray((response as any)?.data?.orders) ? (response as any).data.orders : [];
        const saved = orders.reduce((sum: number, order: any) => {
          const discount = Number(order?.discount ?? order?.discountAmount ?? order?.discount_amount ?? 0);
          return sum + (Number.isFinite(discount) ? discount : 0);
        }, 0);

        if (mounted) {
          setQuickStats({ orders: orders.length, favorites: favoritesCount, saved });
        }
      } catch {
        if (mounted) {
          setQuickStats((prev) => ({ ...prev, favorites: favoritesCount }));
        }
      }
    };

    void loadStats();
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  const formatSaved = (amount: number) => formatCompactMoney(amount) || '0';

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  const handleSignIn = () => {
    setIsOpen(false);
    try {
      const current = `${window.location.pathname || ''}${window.location.search || ''}${window.location.hash || ''}`.trim();
      if (current && !current.startsWith('/customer-sign-')) {
        sessionStorage.setItem('post_login_redirect', current);
      }
    } catch {
      // ignore
    }
    if (onSignIn) {
      onSignIn();
      return;
    }
    window.history.pushState({}, '', '/customer-sign-in');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const handleOpenSettings = () => {
    setIsOpen(false);
    if (onOpenSettings) {
      onOpenSettings();
    }
  };

  const handleOpenOrderHistory = () => {
    setIsOpen(false);
    if (onOpenOrderHistory) {
      onOpenOrderHistory();
    }
  };

  // Get user initials
  const getInitials = (name: string) => {
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0].charAt(0)}${names[1].charAt(0)}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (isGuestUser) {
    // Guests should see a Sign In CTA instead of "Guest" profile.
    return (
      <button
        type="button"
        onClick={handleSignIn}
        className={
          variant === 'compact'
            ? 'inline-flex h-10 items-center gap-2 rounded-xl border border-slate-300/70 bg-slate-900/5 px-3 text-slate-900 shadow-sm transition-colors hover:bg-slate-900/10 active:bg-slate-900/15 dark:border-white/10 dark:bg-white/20 dark:text-white dark:hover:bg-white/30 dark:active:bg-white/40'
            : 'inline-flex items-center gap-2 rounded-xl border border-slate-300/70 bg-slate-900/5 px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm transition-colors hover:bg-slate-900/10 active:bg-slate-900/15 dark:border-white/10 dark:bg-white/20 dark:text-white dark:hover:bg-white/30 dark:active:bg-white/40'
        }
        title="Sign In"
      >
        <User className={variant === 'compact' ? 'h-4 w-4' : 'h-5 w-5'} />
        <span className={variant === 'compact' ? 'whitespace-nowrap text-[12px] font-semibold leading-none' : ''}>
          Sign In
        </span>
      </button>
    );
  }

  if (variant === 'compact') {
    return (
      <div className="relative" ref={dropdownRef}>
        {/* Compact Profile Button */}
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="relative group"
        >
          {user.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="w-10 h-10 rounded-full border-2 border-white shadow-lg group-hover:border-blue-400 transition-all"
            />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center border-2 border-white shadow-lg group-hover:border-blue-400 transition-all">
              <span className="text-white font-bold text-sm">{getInitials(user.name)}</span>
            </div>
          )}
          
          {/* Online indicator */}
          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>
        </button>

        {/* Dropdown Menu */}
        {isOpen && (
          <>
            {/* Backdrop overlay */}
            <div 
              className="fixed inset-0 z-[9998]" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown content */}
            <div className="absolute right-0 mt-2 z-[9999] flex max-h-[min(82vh,42rem)] w-80 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200">
              {/* User Info Header */}
              <div className="shrink-0 bg-gradient-to-r from-blue-600 via-purple-600 to-purple-700 p-5">
                <div className="flex items-center gap-3">
                  {user.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="w-14 h-14 rounded-full border-3 border-white shadow-lg"
                    />
                  ) : (
                    <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-blue-600 font-bold text-xl">{getInitials(user.name)}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-white font-bold text-base truncate">{user.name}</h3>
                    <p className="text-blue-100 text-xs truncate">{user.email}</p>
                    <div className="flex items-center gap-1.5 mt-1.5">
                      <span className="text-xs bg-yellow-400 text-gray-900 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                        <Star className="w-2.5 h-2.5 fill-gray-900 text-gray-900" />
                        Gold Member
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Stats */}
              <div className="grid shrink-0 grid-cols-3 divide-x divide-gray-200 border-b border-gray-200 bg-white">
                <button className="p-3.5 hover:bg-gray-50 transition-colors">
                  <div className="text-center">
                    <p className="text-xl font-bold text-gray-900">{quickStats.orders}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t('profile.quick.orders', 'Orders')}</p>
                  </div>
                </button>
                <button className="p-3.5 hover:bg-gray-50 transition-colors">
                  <div className="text-center">
                    <p className="text-xl font-bold text-gray-900">{quickStats.favorites}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t('profile.quick.favorites', 'Favorites')}</p>
                  </div>
                </button>
                <button className="p-3.5 hover:bg-gray-50 transition-colors">
                  <div className="text-center">
                    <p className="text-xl font-bold text-gray-900">{formatSaved(quickStats.saved)}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{t('profile.quick.saved', 'Saved')}</p>
                  </div>
                </button>
              </div>

              {/* Menu Items */}
              <div className="customer-dropdown-scroll min-h-0 flex-1 overflow-y-auto p-3">
                {/* Appearance Section */}
                <div className="mb-3">
                  <div className="px-2 py-1.5 mb-2">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Appearance</p>
                  </div>
                  <ThemeModeToggle compact />
                </div>

                {/* Dietary Preference Section */}
                <div className="mb-3">
                  <div className="px-2 py-1.5 mb-2">
                    <p className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">Food Preference</p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      onClick={() => handleDietaryModeChange('all')}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg transition-all border-2 ${
                        dietaryMode === 'all'
                          ? 'bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Utensils className="w-4 h-4" />
                      <span className="text-xs font-medium">All</span>
                    </button>
                    <button
                      onClick={() => handleDietaryModeChange('veg')}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg transition-all border-2 ${
                        dietaryMode === 'veg'
                          ? 'bg-green-50 border-green-500 text-green-600'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Leaf className="w-4 h-4" />
                      <span className="text-xs font-medium">Veg</span>
                    </button>
                    <button
                      onClick={() => handleDietaryModeChange('non-veg')}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-lg transition-all border-2 ${
                        dietaryMode === 'non-veg'
                          ? 'bg-red-50 border-red-500 text-red-600'
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Drumstick className="w-4 h-4" />
                      <span className="text-xs font-medium">Non-Veg</span>
                    </button>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="space-y-0.5">
                  {onOpenOrderHistory && (
                    <button
                      onClick={handleOpenOrderHistory}
                      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                    >
                      <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                        <Receipt className="w-4.5 h-4.5 text-blue-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm leading-tight">Order History</p>
                        <p className="text-xs text-gray-500 leading-tight mt-0.5">View past orders</p>
                      </div>
                    </button>
                  )}

                  <button
                    onClick={() => {
                      setIsOpen(false);
                      if (onOpenSettings) onOpenSettings('offers');
                    }}
                    className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                  >
                    <div className="w-9 h-9 bg-pink-50 rounded-lg flex items-center justify-center shrink-0">
                      <Heart className="w-4.5 h-4.5 text-pink-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm leading-tight">Favorites</p>
                      <p className="text-xs text-gray-500 leading-tight mt-0.5">Your saved items</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setIsOpen(false);
                      if (onOpenSettings) onOpenSettings('addresses');
                    }}
                    className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                  >
                    <div className="w-9 h-9 bg-purple-50 rounded-lg flex items-center justify-center shrink-0">
                      <MapPin className="w-4.5 h-4.5 text-purple-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm leading-tight">Addresses</p>
                      <p className="text-xs text-gray-500 leading-tight mt-0.5">Manage locations</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setIsOpen(false);
                      if (onOpenSettings) onOpenSettings('payment');
                    }}
                    className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                  >
                    <div className="w-9 h-9 bg-green-50 rounded-lg flex items-center justify-center shrink-0">
                      <CreditCard className="w-4.5 h-4.5 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm leading-tight">Payments</p>
                      <p className="text-xs text-gray-500 leading-tight mt-0.5">Cards & wallets</p>
                    </div>
                  </button>

                  <button
                    onClick={() => {
                      setIsOpen(false);
                      if (onOpenSettings) onOpenSettings('offers');
                    }}
                    className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                  >
                    <div className="w-9 h-9 bg-orange-50 rounded-lg flex items-center justify-center shrink-0">
                      <Gift className="w-4.5 h-4.5 text-orange-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-sm leading-tight">Offers & Rewards</p>
                      <p className="text-xs text-gray-500 leading-tight mt-0.5">View coupons</p>
                    </div>
                  </button>

                  {onOpenSettings && (
                    <button
                      onClick={handleOpenSettings}
                      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-gray-50 transition-colors text-left group"
                    >
                      <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
                        <Settings className="w-4.5 h-4.5 text-gray-700" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-900 text-sm leading-tight">Settings</p>
                        <p className="text-xs text-gray-500 leading-tight mt-0.5">Preferences & more</p>
                      </div>
                    </button>
                  )}
                </div>

                <div className="h-px bg-gray-200 my-2"></div>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-2 py-2.5 rounded-lg hover:bg-red-50 transition-colors text-left group"
                >
                  <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center shrink-0">
                    <LogOut className="w-4.5 h-4.5 text-red-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-red-600 text-sm leading-tight">Logout</p>
                    <p className="text-xs text-gray-500 leading-tight mt-0.5">Sign out of account</p>
                  </div>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-all group"
      >
        {user.avatar ? (
          <img
            src={user.avatar}
            alt={user.name}
            className="w-8 h-8 rounded-full border-2 border-white shadow-md"
          />
        ) : (
          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center shadow-md">
            <span className="text-blue-600 font-bold text-sm">{getInitials(user.name)}</span>
          </div>
        )}
        <div className="hidden md:block text-left">
          <p className="text-white text-sm font-semibold leading-tight">{user.name.split(' ')[0]}</p>
          <p className="text-white/80 text-xs leading-tight">My Account</p>
        </div>
        <ChevronDown className={`w-4 h-4 text-white transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-[9998]" onClick={() => setIsOpen(false)} />
          <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden z-[9999]">
            {/* Same content as compact variant */}
          </div>
        </>
      )}
    </div>
  );
}
